// Contacts — the store's text list. One row per phone number the store may
// text: who said yes to deals (from the website's list page, a YES by
// text, or added here), who is still only a lead (the number was given for
// their in-store list, nothing more), and who opted out. Every store sees
// it; nothing here sends a text. The rows are `campaign_contacts`, the same
// table the website writes to when a shopper is seated and texted.
import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Download, Phone, RefreshCw, Search, UserPlus, Users, X } from 'lucide-react';
import { supabase } from '../supabaseClient';

const FILTERS = [
  { id: 'in', label: 'Opted in' },
  { id: 'leads', label: 'Leads' },
  { id: 'out', label: 'Opted out' },
  { id: 'all', label: 'All' },
];

const digits = (s) => String(s || '').replace(/\D/g, '');
const prettyPhone = (s) => {
  const d = digits(s).replace(/^1(?=\d{10}$)/, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(s || '');
};
const e164 = (s) => {
  const d = digits(s);
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  return '';
};
const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
};
const sourceLabel = (s) =>
  ({ 'website-seat': 'In store', manual: 'Added here', receipt_import: 'From receipts', review: 'Review', promo_sms: 'Promo', promo_mms: 'Promo' })[s] || (s ? s.replace(/[-_]/g, ' ') : 'Saved');

const bucketOf = (c) => (c.opted_out ? 'out' : c.consent_given ? 'in' : 'leads');

export default function ContactsTab({ storeId, store, addNotification }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('in');
  const [q, setQ] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState('');

  const load = async (silent = false) => {
    if (!storeId) return;
    if (!silent) setLoading(true);
    try {
      const { data, error } = await supabase
        .from('campaign_contacts')
        .select('id, name, phone, source, tags, consent_given, opted_out, opted_out_at, created_at, updated_at, notes')
        .eq('store_id', storeId)
        .order('updated_at', { ascending: false })
        .limit(1000);
      if (error) throw error;
      setRows(data || []);
    } catch (e) {
      console.error('contacts: load failed', e);
      addNotification?.('Could not load contacts', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // The website writes rows as shoppers are seated; keep the list fresh.
    const t = setInterval(() => load(true), 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const counts = useMemo(() => {
    const n = { in: 0, leads: 0, out: 0, all: rows.length };
    for (const c of rows) n[bucketOf(c)] += 1;
    return n;
  }, [rows]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const nd = digits(q);
    return rows.filter((c) => {
      if (filter !== 'all' && bucketOf(c) !== filter) return false;
      if (!needle) return true;
      return (c.name || '').toLowerCase().includes(needle) || (nd && digits(c.phone).includes(nd));
    });
  }, [rows, filter, q]);

  // Added by hand, with consent: the associate is saying the customer asked.
  const add = async () => {
    const p = e164(phone);
    if (!p || adding) return;
    setAdding(true);
    try {
      const dup = rows.find((c) => digits(c.phone).slice(-10) === p.slice(-10));
      if (dup) {
        await setConsent(dup, true);
        setName('');
        setPhone('');
        return;
      }
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('campaign_contacts')
        .insert({ store_id: storeId, phone: p, name: name.trim() || null, source: 'manual', tags: ['added-here'], consent_given: true, opted_out: false, created_at: now, updated_at: now })
        .select()
        .single();
      if (error) throw error;
      setRows((prev) => [data, ...prev]);
      setName('');
      setPhone('');
      addNotification?.(`${data.name || prettyPhone(data.phone)} added`, 'success');
    } catch (e) {
      console.error('contacts: add failed', e);
      addNotification?.('Could not add that number', 'error');
    } finally {
      setAdding(false);
    }
  };

  const patch = async (c, updates) => {
    setBusyId(c.id);
    try {
      const { error } = await supabase.from('campaign_contacts').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', c.id);
      if (error) throw error;
      setRows((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...updates } : x)));
      return true;
    } catch (e) {
      console.error('contacts: update failed', e);
      addNotification?.('Could not update that contact', 'error');
      return false;
    } finally {
      setBusyId('');
    }
  };
  const setConsent = (c, given) => patch(c, { consent_given: given, opted_out: given ? false : c.opted_out, opted_out_at: given ? null : c.opted_out_at });
  const setOut = (c, out) => patch(c, { opted_out: out, opted_out_at: out ? new Date().toISOString() : null });
  const forget = async (c) => {
    if (!window.confirm(`Remove ${c.name || prettyPhone(c.phone)} from the list? This cannot be undone.`)) return;
    setBusyId(c.id);
    try {
      const { error } = await supabase.from('campaign_contacts').delete().eq('id', c.id);
      if (error) throw error;
      setRows((prev) => prev.filter((x) => x.id !== c.id));
    } catch (e) {
      console.error('contacts: delete failed', e);
      addNotification?.('Could not remove that contact', 'error');
    } finally {
      setBusyId('');
    }
  };

  // The list as a file, for whatever sends the texts.
  const exportCsv = () => {
    const head = ['name', 'phone', 'status', 'source', 'added', 'updated'];
    const line = (c) => [c.name || '', e164(c.phone) || c.phone || '', bucketOf(c) === 'in' ? 'opted in' : bucketOf(c) === 'out' ? 'opted out' : 'lead', sourceLabel(c.source), c.created_at || '', c.updated_at || ''].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',');
    const csv = [head.join(','), ...shown.map(line)].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `${(store?.name || 'store').replace(/\s+/g, '-').toLowerCase()}-contacts-${filter}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Contacts</h1>
                <p className="text-sm text-slate-500">
                  {counts.in} opted in · {counts.leads} lead{counts.leads === 1 ? '' : 's'}
                  {counts.out ? ` · ${counts.out} opted out` : ''}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => load()} className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-1.5" title="Reload the list">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button type="button" onClick={exportCsv} disabled={!shown.length} className="h-10 px-3.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5" title="Download the rows shown as a CSV file">
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Add one by hand: a customer who asked at the counter. */}
        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Add someone who asked for texts</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="sm:w-56 px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
            <div className="relative flex-1">
              <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="(555) 123-4567" className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
            </div>
            <button type="button" onClick={add} disabled={adding || !e164(phone)} className={`px-4 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-1.5 ${adding || !e164(phone) ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'}`} title="Add this number as opted in">
              {adding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Add as opted in
            </button>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <div className="flex bg-slate-100 rounded-lg p-1">
            {FILTERS.map((f) => (
              <button key={f.id} type="button" onClick={() => setFilter(f.id)} className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${filter === f.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
                {f.label} <span className="text-slate-400 font-medium">{counts[f.id]}</span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or number" className="w-full pl-10 pr-9 py-2.5 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
            {q && (
              <button type="button" onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200" aria-label="Clear search">
                <X className="w-3 h-3 text-slate-500" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-4">
          {loading && !rows.length ? (
            <div className="py-16 text-center text-sm text-slate-500">Loading…</div>
          ) : shown.length === 0 ? (
            <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-10 text-center">
              <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-sm font-semibold text-slate-700">
                {q ? 'No matches' : filter === 'in' ? 'No one has opted in yet' : filter === 'leads' ? 'No leads yet' : filter === 'out' ? 'No one has opted out' : 'No contacts yet'}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {filter === 'in' ? 'When a shopper taps “Yes, text me deals” on the list the floor texted them, they show up here.' : 'Seat a shopper on the website’s sales desk and text them their list; they land here as a lead.'}
              </p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left font-semibold px-4 py-2.5">Name</th>
                    <th className="text-left font-semibold px-4 py-2.5">Phone</th>
                    <th className="text-left font-semibold px-4 py-2.5">Status</th>
                    <th className="text-left font-semibold px-4 py-2.5 hidden md:table-cell">From</th>
                    <th className="text-left font-semibold px-4 py-2.5 hidden md:table-cell">Updated</th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {shown.map((c) => {
                    const b = bucketOf(c);
                    const busy = busyId === c.id;
                    return (
                      <tr key={c.id} className={b === 'out' ? 'bg-red-50/30' : ''}>
                        <td className="px-4 py-3 font-semibold text-slate-900">{c.name || <span className="text-slate-400 font-normal">No name</span>}</td>
                        <td className="px-4 py-3 font-mono text-slate-700">
                          <a href={`sms:${e164(c.phone) || c.phone}`} className="hover:text-blue-700" title="Open Messages to this number">{prettyPhone(c.phone)}</a>
                        </td>
                        <td className="px-4 py-3">
                          {b === 'in' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold"><CheckCircle2 className="w-3 h-3" /> Opted in</span>
                          ) : b === 'out' ? (
                            <span className="px-2 py-0.5 rounded-full bg-red-100 border border-red-300 text-red-700 text-[11px] font-bold">Opted out</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-[11px] font-bold">Lead</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{sourceLabel(c.source)}</td>
                        <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{when(c.updated_at || c.created_at)}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {b === 'leads' && (
                            <button type="button" disabled={busy} onClick={() => setConsent(c, true)} className="text-xs font-semibold text-blue-700 hover:underline disabled:opacity-50" title="They asked for texts: mark them opted in">Mark opted in</button>
                          )}
                          {b === 'in' && (
                            <button type="button" disabled={busy} onClick={() => setOut(c, true)} className="text-xs font-semibold text-slate-500 hover:text-red-700 hover:underline disabled:opacity-50" title="Stop texting this number">Opt out</button>
                          )}
                          {b === 'out' && (
                            <button type="button" disabled={busy} onClick={() => setOut(c, false)} className="text-xs font-semibold text-slate-500 hover:text-blue-700 hover:underline disabled:opacity-50" title="They asked to come back: opt them back in">Opt back in</button>
                          )}
                          <button type="button" disabled={busy} onClick={() => forget(c)} className="ml-3 text-xs font-semibold text-slate-400 hover:text-red-700 hover:underline disabled:opacity-50" title="Remove this number from the list for good">Remove</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
